import React, { Component, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Popconfirm,
  Progress,
  Row,
  Select,
  Space,
} from "antd";
import { SyncOutlined } from "@ant-design/icons";

import { toast } from "react-toastify";

import CustomSelect from "../CustomFormItem/Select";
import CustomDatePicker from "../CustomFormItem/DatePicker";
import FormMailMultiple from "../FormMailMultiple";

import { formatDateToAPP, round2 } from "../../utils/tools";
import { useApiGet } from "../../hooks/useApi";
import { ecrituresToAdapted } from "../../adapter/ecritureAdapter";

import dayjs from "dayjs";
import TableOwner from "./TableOwner";
import TableSurrender from "./TableSurrender";
import Sepa from "./Sepa";
import Pdf, { prepareRedditionPdf, prepareFactureHonoraires } from "./Pdf";

const { Option, OptGroup } = Select;

// Pause entre deux propriétaires traités par la boucle de génération des relevés PDF.
// Le retry (fetchWithRetry) absorbe les 429 ponctuels, mais sans cette pause la boucle
// enchaîne les appels au rythme du réseau et peut redéclencher un 429 à chaque itération,
// multipliant les tentatives de retry pour rien. Valeur à ajuster avec le backend si la
// limite réelle du rate limiting change.
const PDF_THROTTLE_DELAY_MS = 400;

class FormRedditionView extends Component {
  constructor(props) {
    super(props);

    this.state = {
      preparation: false,
      loading: false,
      showProgress: false,
      percent: 0,
      journal: null,
      detaillee: false,
      longueDuree: false,
      searchOwner: "",
      typePaiement: "1",
      commencees: true,
      reglees: true,
      dateButoir: dayjs().format("DD/MM/YYYY"),
      dateButoirDepenses: dayjs().format("DD/MM/YYYY"),
      dateReleve: dayjs().format("DD/MM/YYYY"),
      lignesReddition: [],
      selectedRowKeys: [],
      selectedRows: [],
      selectedRowRedditionKeys: [],
      selectedRowsReddition: [],
      modalMail: false,
      validation: false,
    };
  }

  formRef = React.createRef();

  componentDidMount() {
    this.init();
    this.props.loadEcrituresAnterieures();
  }

  init = () => {
    this.formRef.current.setFieldsValue({
      journal_id: this.props.journals.filter((item) => item.journal_de_banque === true)[0]
        .journal_id,
      centralises: true,
      detaillee: this.props.parametre.parametre_reddition_detaillee,
    });

    this.setState({
      journal: this.props.journals.filter((item) => item.journal_de_banque === true)[0].journal_id,
      detaillee: this.props.parametre.parametre_reddition_detaillee,
    });
  };

  delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  preparation = (selectedRows) => {
    this.setState({ preparation: true }, async () => {
      await this.delay(500).then(() => this.getLigneReddition(selectedRows));
    });
  };

  getLigneReddition = (selectedRows) => {
    let ibanMissing = false;
    let lignesRedditionFilteredByRows = [];
    if (selectedRows.length > 0) {
      if (this.props.locations && this.props.quittances && this.props.reglements) {
        // Sources comptables du relevé (le store ne contient que la période de l'exercice) :
        // - this.props.ecritures : écritures de la période (ecritures?year=), toutes lignes ;
        // - this.props.ecrituresAnterieures : lignes 411 non redditées antérieures à la période
        //   (ecritures/surrender), écritures partielles réservées au relevé de gestion ;
        // - this.props.report : solde net par compte antérieur à la période (toutes lignes,
        //   redditées ou non).
        // Les montants reversés (montantCompta → montantHT / montantDu) utilisent la période
        // + les lignes antérieures non redditées ; le solde de contrôle utilise la période
        // + le report. Les lignes antérieures ne vont jamais dans le solde : le report les
        // contient déjà.
        const ecrituresReddition = [...this.props.ecritures, ...this.props.ecrituresAnterieures];
        selectedRows.forEach((row) => {
          const proprietaire_id = row.proprietaire_id;

          //LOT DU PROPRIETAIRE
          const lotReferences = [
            ...new Set(
              (this.props.lots || [])
                .filter(
                  (lot) =>
                    lot.lot_proprietaire?.proprietaire_id === proprietaire_id
                )
                .map((lot) => lot.lot_reference)
                .filter(Boolean)
            ),
          ].sort((a, b) => a.localeCompare(b, "fr", { numeric: true }));

          // LOCATIONS
          let locationsFiltered = this.props.locations.filter(
            (location) => location.location_lot.lot_proprietaire.proprietaire_id === proprietaire_id
          );

          // CHECK IF COMMENCEES
          if (this.state.commencees) {
            locationsFiltered = locationsFiltered.filter((location) =>
              dayjs(location.location_date_debut).isSameOrBefore(
                dayjs(this.state.dateButoir, "DD/MM/YYYY")
              )
            );
          }

          // CHECK IF REGLEES
          if (this.state.reglees) {
            const locationsToRemove = [];
            locationsFiltered.forEach((location) => {
              const quittancesFiltered = this.props.quittances.filter(
                (quittance) => quittance.quittance_location_id === location.location_id
              );
              let quittancesLignes = [];
              if (quittancesFiltered.length > 0) {
                quittancesFiltered.forEach((quittance) => {
                  quittance.quittance_ligne.forEach((ligne) => {
                    quittancesLignes.push(ligne);
                  });
                });
              }
              const quittancesFilteredId = quittancesFiltered.map(
                (quittance) => quittance.quittance_id
              );
              const reglementsFiltered = this.props.reglements.filter(
                (reglement) =>
                  quittancesFilteredId.includes(reglement.reglement_quittance.quittance_id) &&
                  // reglement.reglement_reddition === false &&
                  dayjs(reglement.reglement_date).isSameOrBefore(
                    dayjs(this.state.dateButoir, "DD/MM/YYYY")
                  )
              );
              const totalQuittances = quittancesLignes.reduce(
                (a, b) => a + parseFloat(b.quittance_ligne_montant),
                0
              );
              const totalReglements = reglementsFiltered.reduce(
                (a, b) => a + parseFloat(b.reglement_montant),
                0
              );
              if (
                parseFloat(parseFloat(totalQuittances).toFixed(2)) >
                parseFloat(parseFloat(totalReglements).toFixed(2))
              ) {
                locationsToRemove.push(location.location_id);
              }
            });
            locationsFiltered = locationsFiltered.filter(
              (location) => !locationsToRemove.includes(location.location_id)
            );
          }

          const locationsFilteredId = locationsFiltered.map((item) => item.location_id);

          // QUITTANCES
          // CHECK IF COMMENCEES
          let quittancesFiltered = [];
          if (this.state.commencees) {
            quittancesFiltered = this.props.quittances.filter(
              (quittance) =>
                locationsFilteredId.includes(quittance.quittance_location_id) &&
                dayjs(quittance.quittance_date_debut).isSameOrBefore(
                  dayjs(this.state.dateButoir, "DD/MM/YYYY")
                )
            );
          } else {
            quittancesFiltered = this.props.quittances.filter((quittance) =>
              locationsFilteredId.includes(quittance.quittance_location_id)
            );
          }

          let quittancesLignes = [];
          if (quittancesFiltered.length > 0) {
            quittancesFiltered.forEach((quittance) => {
              quittance.quittance_ligne.forEach((ligne) => {
                quittancesLignes.push(ligne);
              });
            });
          }
          const montantQuittances = quittancesLignes.reduce(
            (a, b) => a + parseFloat(b.quittance_ligne_montant),
            0
          );
          const quittancesFilteredId = quittancesFiltered.map(
            (quittance) => quittance.quittance_id
          );

          // ALL REGLEMENTS
          const allReglements = this.props.reglements.filter(
            (reglement) =>
              quittancesFilteredId.includes(reglement.reglement_quittance.quittance_id) &&
              dayjs(reglement.reglement_date).isSameOrBefore(
                dayjs(this.state.dateButoir, "DD/MM/YYYY")
              )
          );

          let montantAllReglements = 0;
          if (allReglements.length > 0) {
            montantAllReglements = allReglements.reduce(
              (a, b) => a + parseFloat(b.reglement_montant),
              0
            );
          }

          // REGLEMENTS
          const reglementsFiltered = this.props.reglements.filter(
            (reglement) =>
              quittancesFilteredId.includes(reglement.reglement_quittance.quittance_id) &&
              reglement.reglement_reddition === false &&
              dayjs(reglement.reglement_date).isSameOrBefore(
                dayjs(this.state.dateButoir, "DD/MM/YYYY")
              )
          );

          // ECRITURES
          const lignesEcritures = [];
          let montantCompta = 0;
          const affectations = row.proprietaire_affectations;
          if (affectations.length > 0) {
            const ecrituresFiltered = ecrituresReddition.filter(
              (ecriture) =>
                (ecriture.ecriture_action === null || ecriture.ecriture_action === "DP") &&
                dayjs(ecriture.ecriture_date_ecriture, "DD/MM/YYYY").isSameOrBefore(
                  dayjs(this.state.dateButoirDepenses, "DD/MM/YYYY")
                )
            );
            ecrituresFiltered &&
              ecrituresFiltered.forEach((ecriture) => {
                const lignes = ecriture.ecriture_ligne.filter(
                  (e) =>
                    e.ecriture_ligne_en_reddition === false &&
                    e.ecriture_ligne_compte.compte_id ===
                      affectations[0].proprietaire_affectation_compte.compte_id
                );
                lignes &&
                  lignes.forEach((ligne) => {
                    if (
                      !ligne.ecriture_ligne_libelle.includes("Regl.") &&
                      !ligne.ecriture_ligne_libelle.includes("Virement solde du") &&
                      !ligne.ecriture_ligne_libelle.includes("Virement acompte du") &&
                      !ligne.ecriture_ligne_libelle.includes("Reddition du") &&
                      !ligne.ecriture_ligne_libelle.includes("Honoraires de gestion")
                    ) {
                      montantCompta += parseFloat(
                        ligne.ecriture_ligne_montant_credit - ligne.ecriture_ligne_montant_debit
                      );
                      ligne.ecriture_id = ecriture.ecriture_id;
                      ligne.ecriture_journal_code = ecriture.ecriture_journal.journal_code;
                      ligne.ecriture_date_ecriture = ecriture.ecriture_date_ecriture;
                      ligne.visible = ecriture.ecriture_action !== "DP";
                      ligne.compta = true;
                      lignesEcritures.push(ligne);
                    }
                  });
              });
            // LIGNE DE DÉPENSES PROPRIÉTAIRES
            const ecrituresDP = ecrituresReddition.filter(
              (ecriture) =>
                ecriture.ecriture_action === "DP" &&
                dayjs(ecriture.ecriture_date_ecriture, "DD/MM/YYYY").isSameOrBefore(
                  dayjs(this.state.dateButoirDepenses, "DD/MM/YYYY")
                )
            );
            const lignesDP = ecrituresDP
              .flatMap((ecriture) => ecriture.ecriture_ligne)
              .filter(
                (e) =>
                  e.ecriture_ligne_en_reddition === false &&
                  e.ecriture_ligne_compte.compte_id ===
                    affectations[0].proprietaire_affectation_compte.compte_id
              );
            const ligneCumulDP = lignesDP.reduce(
              (acc, ligne) => {
                return {
                  ecriture_id: 0,
                  ecriture_journal_code: 0,
                  ecriture_date_ecriture: dayjs().format("DD/MM/YYYY"),
                  ecriture_ligne_id: 0,
                  ecriture_ligne_libelle: `FRAIS À LA CHARGE DU PROPRIÉTAIRE`,
                  visible: true,
                  compta: false,
                  ecriture_ligne_montant_debit:
                    acc.ecriture_ligne_montant_debit +
                    parseFloat(ligne.ecriture_ligne_montant_debit),
                  ecriture_ligne_montant_credit:
                    acc.ecriture_ligne_montant_credit +
                    parseFloat(ligne.ecriture_ligne_montant_credit),
                };
              },
              { ecriture_ligne_montant_debit: 0, ecriture_ligne_montant_credit: 0 }
            );
            lignesEcritures.push(ligneCumulDP);
          }

          const locs = [];
          locs.reglements = [];
          let montantProprietaire = 0;
          let montantFrais = 0;
          reglementsFiltered.forEach((reglement) => {
            const quittance = quittancesFiltered.find(
              (e) => e.quittance_id === reglement.reglement_quittance.quittance_id
            );
            const location = {
              ...locationsFiltered.find((e) => e.location_id === quittance.quittance_location_id),
            };

            let montantReglements = 0;
            let montantReglementsHonoraires = 0;
            if (reglement.reglement_ligne.length > 0) {
              reglement.reglement_ligne.forEach((ligne) => {
                if (
                  ligne.reglement_ligne_quittance_ligne_id.quittance_ligne_rubrique
                    .rubrique_type === "P"
                ) {
                  montantReglements += parseFloat(ligne.reglement_ligne_montant);
                  montantProprietaire += parseFloat(ligne.reglement_ligne_montant);
                  if (
                    ligne.reglement_ligne_quittance_ligne_id.quittance_ligne_rubrique
                      .rubrique_honoraires === true
                  ) {
                    montantReglementsHonoraires += parseFloat(ligne.reglement_ligne_montant);
                  }
                } else if (
                  ligne.reglement_ligne_quittance_ligne_id.quittance_ligne_rubrique
                    .rubrique_type === "G"
                ) {
                  if (
                    ligne.reglement_ligne_quittance_ligne_id.quittance_ligne_rubrique
                      .rubrique_honoraires === true
                  ) {
                    montantReglementsHonoraires += parseFloat(ligne.reglement_ligne_montant);
                  }
                }
              });
            }

            const fraisGestion = this.getFraisGestion(location);

            montantFrais +=
              (parseFloat(fraisGestion) * parseFloat(montantReglementsHonoraires)) / 100;

            location.montant = montantReglements;
            location.dettesLocataires = montantQuittances - montantAllReglements;
            locs.push(location);
          });

          // SOLDE À CE JOUR (colonne de contrôle, n'entre dans aucun montant reversé)
          // = report du compte (solde antérieur à la période) + lignes du compte sur la période.
          // Sans le report, le solde ne porterait que sur la période chargée.
          let totalDebit = 0;
          let totalCredit = 0;

          if (affectations.length > 0) {
            // Report du compte = solde API antérieur à la période chargée
            (this.props.report || [])
              .filter((r) => r.compte_id === affectations[0].proprietaire_affectation_compte.compte_id)
              .forEach((r) => {
                totalDebit += parseFloat(r.montant_debit);
                totalCredit += parseFloat(r.montant_credit);
              });

            // Lignes du compte sur la période (store uniquement, pas les lignes antérieures)
            this.props.ecritures &&
              this.props.ecritures.forEach((ecriture) => {
                const lignes = ecriture.ecriture_ligne.filter(
                  (e) =>
                    e.ecriture_ligne_compte.compte_id ===
                    affectations[0].proprietaire_affectation_compte.compte_id
                );
                lignes &&
                  lignes.forEach((ligne) => {
                    totalDebit += parseFloat(ligne.ecriture_ligne_montant_debit);
                    totalCredit += parseFloat(ligne.ecriture_ligne_montant_credit);
                  });
              });
          }

          let totalSolde = totalCredit - totalDebit;
          // RESULTS
          const ligneReddition = {
            proprietaire_id: row.proprietaire_id,
            proprietaire: row.proprietaire_personne,
            proprietaire_affectations: row.proprietaire_affectations,
            proprietaire_code: row.proprietaire_code,
            proprietaire_nom: row.proprietaire_personne.personne_nom,
            proprietaire_prenom: row.proprietaire_personne.personne_prenom,
            lot_reference: lotReferences,
            locations: locs,
            lignesEcritures: lignesEcritures,
            reglements: reglementsFiltered,
            montantProprietaire: parseFloat(montantProprietaire),
            montantTotal: parseFloat(montantProprietaire + montantCompta),
            montantFrais: montantFrais,
            montantHT: parseFloat(parseFloat(montantProprietaire + montantCompta)),
            montantDu: parseFloat(montantProprietaire + montantCompta - montantFrais),
            solde: totalSolde,
            dettesLocataires: locs.reduce((a, b) => a + parseFloat(b.dettesLocataires), 0),
          };

          if (
            row.proprietaire_personne.personne_compte_bancaire.compte_bancaire_type_paiement
              .type_paiement_libelle === "Virement" &&
            !row.proprietaire_personne.personne_compte_bancaire.compte_bancaire_IBAN
          ) {
            // ibanMissing = true;
          }

          if (ligneReddition.montant !== 0) {
            lignesRedditionFilteredByRows.push(ligneReddition);
          }
        });
      }
    }
    if (!ibanMissing) {
      this.setState({
        lignesReddition: lignesRedditionFilteredByRows,
        // selectedRowKeys: [],
        // selectedRows: [],
        showProgress: false,
        selectedRowRedditionKeys: [],
        selectedRowsReddition: [],
        preparation: false,
        loading: false,
      });
    } else {
      this.setState({ preparation: false }, () =>
        toast.error(
          "Informations bancaires incomplètes pour au moins un des propriétaire sélectionné !",
          { containerId: "A" }
        )
      );
    }
  };

  getFraisGestion = (location) => {
    let fraisGestion = 0;
    switch (location.location_type_contrat) {
      case 1:
        fraisGestion = location.location_lot.lot_frais_gestion_alur
          ? location.location_lot.lot_frais_gestion_alur
          : 0;
        break;
      case 2:
        fraisGestion = location.location_lot.lot_frais_gestion_lt
          ? location.location_lot.lot_frais_gestion_lt
          : 0;
        break;
      case 3:
        fraisGestion = location.location_lot.lot_frais_gestion_ct
          ? location.location_lot.lot_frais_gestion_ct
          : 0;
        break;
      default:
        fraisGestion = location.location_lot.lot_frais_gestion_ct
          ? location.location_lot.lot_frais_gestion_ct
          : 0;
        break;
    }
    return fraisGestion;
  };

  onChangeJournal = (id) => {
    this.setState({ journal: id });
  };

  onChangeTypePaiement = (value) => {
    this.setState({ typePaiement: value });
  };

  onChangeDateButoir = (e) => {
    this.setState({ dateButoir: e, dateButoirDepenses: e }, () => {
      this.formRef.current.setFieldsValue({
        dateDepenses: dayjs(e, "DD/MM/YYYY"),
      });
    });
  };

  onChangeDateButoirDepenses = (e) => {
    this.setState({ dateButoirDepenses: e });
  };

  onChangeDateReleve = (e) => {
    this.setState({ dateReleve: e });
  };

  onChangeCommencees = (value) => {
    this.setState({ commencees: value.target.checked });
  };

  onChangeReglees = (value) => {
    this.setState({ reglees: value.target.checked });
  };

  onChangeDetaillee = (value) => {
    this.setState(
      {
        detaillee: value.target.checked,
        reglees: value.target.checked ? false : this.state.reglees,
      },
      () => {
        const filtresActuels = this.formRef.current.getFieldValue("filtres") || [];
        console.log(value.target.checked, "value.target.checked");

        const filtres =
          value.target.checked === true
            ? filtresActuels.filter((f) => f !== "reglees")
            : filtresActuels;
        this.formRef.current.setFieldsValue({ filtres });
      }
    );
  };

  onChangeLongueDuree = (value) => {
    this.setState({ longueDuree: value.target.checked });
  };

  onChangeSearchOwner = (value) => {
    this.setState({ searchOwner: value });
  };

  onSubmitForm = () => {
    this.formRef.current.submit();
  };

  // A factoriser: présent dans FormMailMultiple
  onFinishReddtion = (data) => {
    this.setState({ showProgress: true, loading: true }, () => {
      const journalBanque = this.props.journals.find((item) => item.journal_id === data.journal_id);
      const journalHonoraire = this.props.journals.find((item) => item.journal_code === "70");
      if (journalBanque.journal_compte && journalHonoraire.journal_compte) {
        this.generateCompta(data, journalBanque, journalHonoraire);
      } else {
        toast.error("Le jounal sélectionné n'a pas de compte de contrepartie !", {
          containerId: "A",
        });
        this.setState({ loading: false });
      }
    });
  };

  generateCompta = (data, journalBanque, journalHonoraire) => {
    this.state.selectedRowsReddition.some((ligne) => ligne.montantHT > 0) &&
    data.centralises === true
      ? this.createEcritureBanque(this.state.selectedRowsReddition, journalBanque)
      : this.state.selectedRowsReddition.forEach((selectedRow) => {
          this.createEcritureBanque([selectedRow], journalBanque);
        });
    this.state.selectedRowsReddition.some((ligne) => ligne.montantFrais > 0) &&
      this.createEcritureHonoraires(this.state.selectedRowsReddition, journalHonoraire);
    // Règlements en_reddition
    const reglements = [];
    this.state.selectedRowsReddition.forEach((ligne) => {
      ligne.reglements.forEach((reglement) => {
        reglements.push(reglement);
      });
    });
    this.props.updateReglements({ reglements: reglements }).then(() => {
      // Lignes ecritures en_reddition
      const ecritures = [];
      this.state.selectedRowsReddition.forEach((ligneReddition) => {
        ligneReddition.lignesEcritures
          .filter((ligne) => ligne.compta)
          .forEach((ligne) => {
            const ecriture =
              this.props.ecritures.find((ecriture) => ligne.ecriture_id === ecriture.ecriture_id) ||
              this.props.ecrituresAnterieures.find(
                (ecriture) => ligne.ecriture_id === ecriture.ecriture_id
              );
            if (ecriture) {
              ecriture.ecriture_ligne.forEach((ligneEcriture) => {
                if (ligneEcriture.ecriture_ligne_id === ligne.ecriture_ligne_id) {
                  ligneEcriture.ecriture_ligne_en_reddition = true;
                }
              });
              ecritures.push(ecriture);
            }
          });
      });
      this.props.updateEcritures({ ecritures: ecritures }).then(() => {
        // Lignes antérieures passées en reddition : rechargement pour ne pas les reprendre
        if (ecritures.some((ecriture) => this.props.ecrituresAnterieures.includes(ecriture))) {
          this.props.loadEcrituresAnterieures();
        }
        this.compteurFactureHonoraires = { current: 0 };
        this.prepareLigne(0, this.state.selectedRowsReddition.length);
      });
    });
  };

  createEcritureBanque = (selectedRows, journal) => {
    const ecritureLignes = [];
    let montant = 0;
    selectedRows.forEach((row) => {
      if (row.montantHT > 0) {
        const ligneDebit = {
          ecriture_ligne_libelle: `Relevé de gestion du ${
            this.state.dateReleve
              ? dayjs(this.state.dateReleve, "DD/MM/YYYY").format("DD/MM/YYYY")
              : dayjs().format("DD/MM/YYYY")
          }`,
          ecriture_ligne_montant_debit: parseFloat(row.montantHT - row.montantFrais).toFixed(2),
          ecriture_ligne_montant_credit: 0,
          ecriture_ligne_compte_id:
            row.proprietaire_affectations[0].proprietaire_affectation_compte.compte_id,
          ecriture_ligne_locataire_code: "",
          ecriture_ligne_en_reddition: true,
        };
        montant += parseFloat(row.montantHT - row.montantFrais);
        ecritureLignes.push(ligneDebit);
      }
    });
    const ligneCredit = {
      ecriture_ligne_libelle: `Relevé de gestion du ${
        this.state.dateReleve
          ? dayjs(this.state.dateReleve, "DD/MM/YYYY").format("DD/MM/YYYY")
          : dayjs().format("DD/MM/YYYY")
      }`,
      ecriture_ligne_montant_debit: 0,
      ecriture_ligne_montant_credit: parseFloat(montant).toFixed(2),
      ecriture_ligne_compte_id: journal.journal_compte.compte_id,
      ecriture_ligne_locataire_code: "",
      ecriture_ligne_en_reddition: true,
    };
    ecritureLignes.push(ligneCredit);
    const ecriture = {
      ecriture_journal_id: journal.journal_id,
      ecriture_date_ecriture: this.state.dateReleve
        ? dayjs(this.state.dateReleve, "DD/MM/YYYY").format("YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD"),
      ecriture_libelle: `Relevé de gestion du ${
        this.state.dateReleve
          ? dayjs(this.state.dateReleve, "DD/MM/YYYY").format("DD/MM/YYYY")
          : dayjs().format("DD/MM/YYYY")
      }`,
      ecriture_ligne: ecritureLignes,
    };
    //console.log('Ecriture Banque', ecriture);
    this.props
      .saveEcriture(ecriture)
      .catch((err) => toast.error("Erreur création écriture banque !", { containerId: "A" }));
  };

  createEcritureHonoraires = (selectedRows, journal) => {
    const ecritureLignes = [];
    let montant = 0;
    selectedRows.forEach((row) => {
      if (row.montantFrais !== 0) {
        const ligneDebit = {
          ecriture_ligne_libelle: `Honoraires de gestion TTC sur ${parseFloat(
            row.montantProprietaire
          ).toFixed(2)}€`,
          ecriture_ligne_montant_debit: parseFloat(row.montantFrais).toFixed(2),
          ecriture_ligne_montant_credit: 0,
          ecriture_ligne_compte_id:
            row.proprietaire_affectations[0].proprietaire_affectation_compte.compte_id,
          ecriture_ligne_locataire_code: "",
          ecriture_ligne_en_reddition: true,
        };
        montant += parseFloat(Number(parseFloat(row.montantFrais).toFixed(2)));
        ecritureLignes.push(ligneDebit);
      }
    });
    const ligneCredit = {
      ecriture_ligne_libelle: `Honoraires de gestion TTC`,
      ecriture_ligne_montant_debit: 0,
      ecriture_ligne_montant_credit: parseFloat(montant).toFixed(2),
      ecriture_ligne_compte_id: journal.journal_compte.compte_id,
      ecriture_ligne_locataire_code: "",
      ecriture_ligne_en_reddition: true,
    };
    ecritureLignes.push(ligneCredit);
    const ecriture = {
      ecriture_journal_id: journal.journal_id,
      ecriture_date_ecriture: this.state.dateReleve
        ? dayjs(this.state.dateReleve, "DD/MM/YYYY").format("YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD"),
      ecriture_libelle: `Honoraires de gestion TTC`,
      ecriture_ligne: ecritureLignes,
    };
    //console.log('Ecriture Honoraires', ecriture);
    this.props
      .saveEcriture(ecriture)
      .catch((err) => toast.error("Erreur création écriture honoraires !", { containerId: "A" }));
  };

  prepareLigne = (i, len) => {
    if (i < len) {
      const ligne = this.state.selectedRowsReddition[i];
      if (ligne.proprietaire_affectations.length > 0) {
        const dossier = ligne.proprietaire_affectations[0].proprietaire_affectation_dossier;
        if (dossier !== null) {
          this.setState({ percent: parseInt((i / len) * 100) }, () => {
            this.preparePdf(i, ligne, ligne.proprietaire_affectations[0]);
          });
        } else {
          this.props.saveDossier(this.getJsonDossier(ligne)).then((dossier) => {
            this.props
              .updateProprietaireAffectation(
                {
                  proprietaire_affectation_compte_id:
                    ligne.proprietaire_affectations[0].proprietaire_affectation_compte_id,
                  proprietaire_affectation_dossier_id: dossier ? dossier.dossier_id : null,
                },
                ligne.proprietaire_id,
                ligne.proprietaire_affectations[0].proprietaire_affectation_societe.societe_id
              )
              .then((affectation) => {
                this.setState({ percent: parseInt((i / len) * 100) }, () => {
                  this.preparePdf(i, ligne, affectation);
                });
              });
          });
        }
      }
    } else {
      toast.success("Relevé de gestion validé et comptabilisé !", { containerId: "A" });
      if (this.compteurFactureHonoraires && this.compteurFactureHonoraires.current > 0) {
        const societe = this.props.utilisateur.utilisateur_societe;
        const newInvoiceCounter = (
          parseInt(societe.invoice_counter, 10) + this.compteurFactureHonoraires.current
        )
          .toString()
          .padStart(4, "0");
        this.props
          .incrementInvoiceCounter(newInvoiceCounter)
          .catch(() =>
            toast.error("Erreur lors de la mise à jour du compteur de facturation !", {
              containerId: "A",
            })
          );
      }
      this.setState({
        loading: false,
        percent: 100,
        lignesReddition: [],
        modalMail: !this.state.validation,
      });
    }
  };

  // A factoriser: présent dans FormMailMultiple
  getJsonDossier = (proprietaire) => {
    return {
      dossier_url: `Proprietaires/${proprietaire.proprietaire_id}/`,
    };
  };

  // Les écritures comptables et règlements de tout le lot sont déjà comptabilisés
  // (generateCompta, avant le démarrage de cette boucle) : on ne peut donc pas se contenter
  // de sauter silencieusement un propriétaire en échec de génération de document, sous peine
  // de laisser des écritures "en reddition" sans relevé/facture associé. En cas d'échec
  // (après les tentatives de fetchWithRetry), on interrompt explicitement la boucle et on
  // débloque l'UI, plutôt que de continuer ou de rester figé indéfiniment.
  handlePreparePdfError = (ligne, err) => {
    console.error(err);
    toast.error(
      `Échec de génération du document pour ${ligne.proprietaire_nom} : ses écritures sont comptabilisées mais le document n'a pas été généré. Relevé de gestion interrompu, réessayez pour ce propriétaire.`,
      { containerId: "A", autoClose: false }
    );
    this.setState({ loading: false, showProgress: false });
  };

  preparePdf = (i, ligne, affectation) => {
    prepareRedditionPdf({
      utilisateur: this.props.utilisateur,
      dateButoir: this.state.dateButoir,
      detaillee: this.state.detaillee,
      tva: this.props.parametre.parametre_reddition_tva,
      ligne,
      affectation,
      tags: this.props.tags,
      saveDocument: this.props.saveDocument,
      onError: (err) => this.handlePreparePdfError(ligne, err),
      onSaved: (document) => {
        ligne.document = document;
        prepareFactureHonoraires({
          utilisateur: this.props.utilisateur,
          dateButoir: this.state.dateButoir,
          ligne,
          affectation,
          tags: this.props.tags,
          saveDocument: this.props.saveDocument,
          compteurFactureHonoraires: this.compteurFactureHonoraires,
          onError: (err) => this.handlePreparePdfError(ligne, err),
          onSaved: (factureDocument) => {
            if (factureDocument) {
              ligne.factureHonoraires = factureDocument;
            }
            // Throttle : on laisse respirer l'API de rendu PDF avant de passer au
            // propriétaire suivant (voir commentaire sur PDF_THROTTLE_DELAY_MS).
            setTimeout(() => {
              this.prepareLigne(i + 1, this.state.selectedRowsReddition.length);
            }, PDF_THROTTLE_DELAY_MS);
          },
        });
      },
    });
  };

  prepareMailing = () => {
    this.setState({ modalMail: true, validation: true });
  };

  closeModalMail = () => {
    this.setState({ modalMail: false });
  };

  onClickAfficher = (key, title, proprietaire) => {
    // const pane = { key, title, content: "" };
    this.props.addPanesPersonne(proprietaire, true, true);
  };

  /*setBackgroundColor = (item) => {
    if (round2(item.solde) < round2(item.montantHT) || round2(item.solde) < 0) {
      return "editable-row row-edited-bg";
    } else {
      return "editable-row";
    }
  };*/

  checkDebit = (lignesReddition) => {
    return lignesReddition.some((ligne) => ligne.montantHT <= 0);
  };

  handleAnnulation = () => {
    this.setState({
      lignesReddition: [],
      selectedRowKeys: [],
      selectedRows: [],
      selectedRowRedditionKeys: [],
      selectedRowsReddition: [],
    });
  };

  render() {
    const {
      preparation,
      loading,
      showProgress,
      percent,
      journal,
      typePaiement,
      dateButoir,
      dateButoirDepenses,
      detaillee,
      dateReleve,
      lignesReddition,
      selectedRowKeys,
      selectedRows,
      selectedRowRedditionKeys,
      selectedRowsReddition,
      modalMail,
      searchOwner,
    } = this.state;
    const { journals } = this.props;

    const rowSelection = {
      onChange: (selectedRowKeys, selectedRows) => {
        this.setState({ selectedRowKeys, selectedRows });
      },
      selectedRowKeys: selectedRowKeys,
    };

    const rowRedditionSelection = {
      onChange: (selectedRowRedditionKeys, selectedRowsReddition) => {
        // console.log("Clés sélectionnées :", selectedRowRedditionKeys);
        // console.log("Lignes sélectionnées :", selectedRowsReddition);
        this.setState({ selectedRowRedditionKeys, selectedRowsReddition });
      },
      getCheckboxProps: (record) => {
        // disabled: parseInt(record.solde + record.montantHT + record.montantDu + record.dettesLocataires + record.montantFrais) <= 0,
          const total =
          round2(parseFloat(record.solde || 0) +
          parseFloat(record.montantHT || 0) +
          parseFloat(record.montantDu || 0) +
          parseFloat(record.dettesLocataires || 0) +
          parseFloat(record.montantFrais || 0));
          // console.log(`Propriétaire ${record.proprietaire_nom} - Total calculé :`, total);
        return {
          disabled: total === 0,
           // name: record.proprietaire_nom,
        };
      },
      selectedRowKeys: selectedRowRedditionKeys,
    };

    const hasDebit = this.checkDebit(selectedRowsReddition);

    return (
      <Form
        ref={this.formRef}
        onFinish={this.onFinishReddtion}
        initialValues={{ type_paiement: typePaiement }}
        style={{ padding: "20px" }}
      >
        <Modal
          width="800px"
          footer={null}
          title="Mailing"
          open={modalMail}
          onCancel={this.closeModalMail}
        >
          <FormMailMultiple
            utilisateur={this.props.utilisateur}
            dateButoir={dateButoir}
            detaillee={detaillee}
            parametre={this.props.parametre}
            tags={this.props.tags}
            lignesReddition={selectedRowsReddition}
            saveEmail={this.props.saveEmail}
            saveDossier={this.props.saveDossier}
            saveDocument={this.props.saveDocument}
            updateProprietaireAffectation={this.props.updateProprietaireAffectation}
          />
        </Modal>

        <Divider orientation="left">Paramètres du relevé</Divider>
        <Row gutter={[16, 8]}>
          <Col xs={24} md={6}>
            <CustomSelect
              label="Type de paiement"
              inputName="type_paiement"
              formItemName="type_paiement"
              objectValue={typePaiement}
              onChange={(value) => this.onChangeTypePaiement(value)}
            >
              <OptGroup label="Type">
                <Option label="Virement" value="1">
                  Virement
                </Option>
                <Option label="Chèque" value="2">
                  Chèque
                </Option>
              </OptGroup>
            </CustomSelect>
          </Col>
          <Col xs={24} md={6}>
            <CustomSelect
              label="Journal"
              inputName="journal_id"
              formItemName="journal_id"
              objectValue={journal}
              onChange={(id) => this.onChangeJournal(id)}
            >
              <OptGroup label="Journal">
                {journals &&
                  journals.map((item, i) => {
                    return (
                      <Option
                        key={i}
                        value={item.journal_id}
                        label={item.journal_code + " - " + item.journal_libelle}
                      >
                        <Row>
                          <Col sm={8}>
                            {item.journal_code} - {item.journal_libelle}
                          </Col>
                        </Row>
                      </Option>
                    );
                  })}
              </OptGroup>
            </CustomSelect>
          </Col>
          <Col xs={24} md={6}>
            <CustomDatePicker
              label="Date butoir encaissement"
              inputName="date"
              formItemName="date"
              onChange={(e) => this.onChangeDateButoir(e)}
              objectValue={dateButoir}
              today={true}
            />
          </Col>
          <Col xs={24} md={6}>
            <CustomDatePicker
              label="Date butoir dépenses"
              inputName="dateDepenses"
              formItemName="dateDepenses"
              onChange={(e) => this.onChangeDateButoirDepenses(e)}
              objectValue={dateButoirDepenses}
              today={true}
            />
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Space wrap size={[24, 8]}>
              <Form.Item name="filtres" initialValue={["commencees", "reglees"]} style={{ marginBottom: 0 }}>
                <Checkbox.Group>
                  <Checkbox value="commencees" onChange={(value) => this.onChangeCommencees(value)}>
                    {`Périodes commencées avant le ${dayjs(dateButoir, "DD/MM/YYYY").format(
                      "DD/MM/YYYY"
                    )} `}
                  </Checkbox>
                  <Checkbox value="reglees" onChange={(value) => this.onChangeReglees(value)}>
                    {`Périodes reglées en totalité avant le ${dayjs(dateButoir, "DD/MM/YYYY").format(
                      "DD/MM/YYYY"
                    )} `}
                  </Checkbox>
                </Checkbox.Group>
              </Form.Item>
              <Form.Item name="centralises" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Checkbox>Règlements centralisés</Checkbox>
              </Form.Item>
              <Form.Item name="detaillee" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Checkbox onChange={(value) => this.onChangeDetaillee(value)}>
                  Rapport de gestion détaillé
                </Checkbox>
              </Form.Item>
            </Space>
          </Col>
        </Row>

        <Divider orientation="left">Sélection des propriétaires</Divider>
        <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
          <Col>
            <Input.Search
              placeholder="Rechercher un propriétaire"
              allowClear
              value={searchOwner}
              onChange={(e) => this.onChangeSearchOwner(e.target.value)}
              style={{ width: 260 }}
            />
          </Col>
          <Col>
            <Space align="center">
              {this.props.erreurAnterieures && (
                <Alert
                  type="error"
                  showIcon
                  message="Écritures antérieures à la période non chargées, préparation impossible"
                />
              )}
              <Form.Item name="longueDuree" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Checkbox onChange={(value) => this.onChangeLongueDuree(value)}>
                  Longue durée uniquement
                </Checkbox>
              </Form.Item>
              <Button
                type="primary"
                icon={<SyncOutlined />}
                loading={preparation || this.props.loadingAnterieures}
                disabled={!this.props.anterieuresChargees}
                onClick={() => this.preparation(selectedRows)}
              >
                Préparation
              </Button>
            </Space>
          </Col>
        </Row>
        <TableOwner
          openTabs={this.onClickAfficher}
          longTerm={this.state.longueDuree}
          payment={this.state.typePaiement}
          owners={this.props.proprietaires}
          lots={this.props.lots}
          search={searchOwner}
          rowSelection={rowSelection}
        />

        <Divider orientation="left">Relevés préparés</Divider>
        <TableSurrender
          lignes={lignesReddition}
          rowSelection={rowRedditionSelection}
          selectedRowsReddition={selectedRowsReddition}
        />
        {showProgress && <Progress percent={percent} style={{ marginTop: 12 }} />}
        {lignesReddition.length > 0 && (
          <Row justify="end" style={{ marginTop: 12 }}>
            <Space wrap>
              <Pdf
                utilisateur={this.props.utilisateur}
                dateButoir={dateButoir}
                detaillee={detaillee}
                tva={this.props.parametre.parametre_reddition_tva}
                lignesReddition={selectedRowsReddition}
              >
                {typePaiement === "1" && (
                  <Sepa
                    journal={journals.find((item) => item.journal_id === journal)}
                    lignesReddition={selectedRowsReddition}
                    raisonSociale={
                      this.props.utilisateur.utilisateur_societe.societe_raison_sociale
                    }
                  />
                )}
              </Pdf>
              {/* <Button type="primary" onClick={() => this.prepareMailing()}>
                Envoyer par mail
              </Button> */}
            </Space>
          </Row>
        )}

        {lignesReddition.length > 0 && (
          <>
            <Divider style={{ margin: "16px 0" }} />
            <Row justify="space-between" align="middle">
              <Col>
                {hasDebit && (
                  <Alert
                    type="warning"
                    showIcon
                    message="Au moins un des relevés est débiteur, validation impossible"
                  />
                )}
              </Col>
              <Col>
                <Space align="center">
                  <div className="date-releve-inline">
                    <CustomDatePicker
                      label="Date de relevé"
                      inputName="date_releve"
                      formItemName="date_releve"
                      onChange={(e) => this.onChangeDateReleve(e)}
                      objectValue={dateReleve}
                      today={true}
                    />
                  </div>
                  <Button type="primary" size="large" onClick={() => this.handleAnnulation()} danger>
                    Annulation
                  </Button>
                  <Popconfirm
                    title="Êtes-vous sûr de vouloir lancer la validation des relevés de gestion ? Attention, après validation l'impression des rapports ne sera plus possible"
                    onConfirm={() => this.onSubmitForm()}
                  >
                    <Button
                      type="primary"
                      size="large"
                      loading={loading}
                      disabled={hasDebit || selectedRowsReddition.length === 0}
                    >
                      Validation
                    </Button>
                  </Popconfirm>
                </Space>
              </Col>
            </Row>
          </>
        )}
      </Form>
    );
  }
}

// Wrapper fonctionnel : porte l'appel ecritures/surrender (hook) pour le composant de classe
export default function FormReddition(props) {
  // Exercice sélectionné dans le header, comme pour le chargement des écritures
  const annee = useSelector((state) => state.exercice.annee);
  const {
    execute: loadEcrituresAnterieures,
    data,
    loading,
    error,
  } = useApiGet(`ecritures/surrender?year=${annee}`);

  // Lignes non redditées antérieures à la période, au format des écritures du store
  const ecrituresAnterieures = useMemo(
    () =>
      (ecrituresToAdapted(data?.surrender) ?? []).map((ecriture) => ({
        ...ecriture,
        ecriture_date_ecriture: formatDateToAPP(ecriture.ecriture_date_ecriture),
        ecriture_date_saisie: formatDateToAPP(ecriture.ecriture_date_saisie),
      })),
    [data]
  );

  return (
    <FormRedditionView
      {...props}
      loadEcrituresAnterieures={loadEcrituresAnterieures}
      ecrituresAnterieures={ecrituresAnterieures}
      anterieuresChargees={data !== null}
      loadingAnterieures={loading}
      erreurAnterieures={error}
    />
  );
}
